/* eslint-disable better-mutation/no-mutation */

import myTraderaItemsSelector from './items';
import { logger } from 'packages/logger';
import {
    findKeysByPurchaseOrderId,
    findKeysByTransactionId,
    findKeysByItemId
} from '../../helpers/commands-helper.js';
import { isServer } from 'tradera/utils/nextjs';

if (isServer) {
    // Do not remove this without also removing the disable of eslint better-mutation/no-mutation at top of file.
    throw new Error(
        'This implementation is not compatible with SSR as global properties are shared between server instances'
    );
}

const getSoldItemStateFor = (sellerState) => {
    if (sellerState.shipped && sellerState.feedbackLeft) {
        return 'Done';
    }
    if (sellerState.shipped) {
        return 'Shipped';
    }
    return sellerState.paid ? 'Paid' : 'NotPaid';
};

const updateStateAndLabels = (item, listViewType, messagingParticipation) => {
    if (listViewType === undefined) {
        try {
            throw 'updateStateAndLabels: listViewType === undefined, ' + item;
        } catch (e) {
            logger(e);
        }
    }
    item.transaction.sellerState.state = getSoldItemStateFor(
        item.transaction.sellerState
    );
    return myTraderaItemsSelector.getSoldItem(
        item,
        listViewType,
        messagingParticipation
    );
};

const updateUnsoldLabelsAndActions = (item, listViewType) => {
    if (listViewType === undefined) {
        try {
            throw (
                'updateUnsoldLabelsAndActions: listViewType === undefined, ' +
                item
            );
        } catch (e) {
            logger(e);
        }
    }
    return myTraderaItemsSelector.getUnsoldItem(item, listViewType);
};

const setPaidState =
    (flag, listViewType, messagingParticipation) => (items) => (keys) => {
        return keys.map((key) => {
            const item = items[key];
            if (!item) {
                try {
                    throw `setPaidState failed. tried to find key ${key} in ${JSON.stringify(
                        items
                    )}`;
                } catch (e) {
                    logger(e);
                }
            }

            item.transaction.sellerState.paid = flag;
            return updateStateAndLabels(
                item,
                listViewType,
                messagingParticipation
            );
        });
    };

const setShippedState = (flag, listViewType) => (items) => (key) => {
    const item = items[key];
    if (!item) {
        try {
            throw `setShippedState failed. tried to find key ${key} in ${JSON.stringify(
                items
            )}`;
        } catch (e) {
            logger(e);
        }
    }

    item.transaction.sellerState.shipped = flag;
    return updateStateAndLabels(item, listViewType);
};

const setTransactionHiddenState = (flag, listViewType) => (items) => (key) => {
    const item = items[key];
    if (!item) {
        try {
            throw `setTransactionHiddenState failed. tried to find key ${key} in ${JSON.stringify(
                items
            )}`;
        } catch (err) {
            logger(err);
        }
        return [];
    }

    item.transaction.sellerState.hidden = flag;
    return updateStateAndLabels(item, listViewType);
};

const setUnsoldItemHiddenState =
    (flag, listViewType) => (items, memberId) => (key) => {
        const item = items[key];
        if (!item) {
            try {
                throw `setUnsoldItemHiddenState failed. tried to find key ${key} in ${JSON.stringify(
                    items
                )}`;
            } catch (err) {
                logger(err);
            }
            return [];
        }

        if (!item.hiddenByMember?.includes(memberId)) {
            item.removeFromList = true;
        }
        return updateUnsoldLabelsAndActions(item, listViewType, memberId);
    };

const setFeedback = (flag, listViewType) => (items) => (key) => {
    const item = items[key];
    if (!item) {
        try {
            throw `setFeedback failed. tried to find key ${key} in ${JSON.stringify(
                items
            )}`;
        } catch (err) {
            logger(err);
        }
        return [];
    }

    item.transaction.sellerState.feedbackLeft = flag;
    return updateStateAndLabels(item, listViewType);
};

const setShippingBookedState = (flag, listViewType) => (items) => (key) => {
    const item = items[key];
    if (!item) {
        try {
            throw `setShippingBookedState failed. tried to find key ${key} in ${JSON.stringify(
                items
            )}`;
        } catch (err) {
            logger(err);
        }
        return [];
    }

    item.transaction.sellerState.shippingBooked = flag;
    return updateStateAndLabels(item, listViewType);
};

const setShippingDeletedState = (listViewType) => (items) => (key) => {
    const item = items[key];
    if (!item) {
        try {
            throw `setShippingDeletedState failed. tried to find key ${key} in ${JSON.stringify(
                items
            )}`;
        } catch (err) {
            logger(err);
        }
        return [];
    }

    item.transaction.hasUnpaidShipping = false;
    return updateStateAndLabels(item, listViewType);
};

const setItemsHasRestartedChild = (flag, listViewType) => (items) => (key) => {
    const item = items[key];
    if (!item) {
        try {
            throw `setItemHasRestartedChild failed. tried to find key ${key} in ${JSON.stringify(
                items
            )}`;
        } catch (err) {
            logger(err);
        }
        return [];
    }

    item.listing.restart.hasRestartedChild = flag;
    return updateUnsoldLabelsAndActions(item, listViewType);
};

export const markAsPaidBySellerReducer = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setPaidState(
        true,
        action.payload.listViewType,
        action.payload.messagingParticipation
    )(stateItems);
    const findUpdatedKeysByPurchaseOrderId =
        findKeysByPurchaseOrderId(stateItems);
    const updatedOrders = action.payload.ids
        .map(findUpdatedKeysByPurchaseOrderId)
        .map(updateChangedItems);

    updatedOrders.forEach((updatedItems) => {
        updatedItems.forEach((updatedItem) => {
            stateItems[updatedItem.key] = updatedItem;
        });
    });

    return stateItems;
};

export const unmarkAsPaidBySellerReducer = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setPaidState(
        false,
        action.payload.listViewType,
        action.payload.messagingParticipation
    )(stateItems);
    const findUpdatedKeysByPurchaseOrderId =
        findKeysByPurchaseOrderId(stateItems);
    const updatedOrders = action.payload.ids
        .map(findUpdatedKeysByPurchaseOrderId)
        .map(updateChangedItems);

    updatedOrders.forEach((updatedItems) => {
        updatedItems.forEach((updatedItem) => {
            stateItems[updatedItem.key] = updatedItem;
        });
    });

    return stateItems;
};

export const markAsShippedReducer = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setShippedState(
        true,
        action.payload.listViewType
    )(stateItems);
    const findUpdatedKeysByTransactionId = findKeysByTransactionId(stateItems);
    const updatedItems = action.payload.ids
        .map(findUpdatedKeysByTransactionId)
        .map(updateChangedItems);

    updatedItems.forEach((updatedItem) => {
        stateItems[updatedItem.key] = updatedItem;
    });

    return stateItems;
};

export const unmarkAsShippedReducer = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setShippedState(
        false,
        action.payload.listViewType
    )(stateItems);
    const findUpdatedKeysByTransactionId = findKeysByTransactionId(stateItems);
    const updatedItems = action.payload.ids
        .map(findUpdatedKeysByTransactionId)
        .map(updateChangedItems);

    updatedItems.forEach((updatedItem) => {
        stateItems[updatedItem.key] = updatedItem;
    });

    return stateItems;
};

export const markTransactionAsHiddenBySellerReducer = (state, action) => {
    const updatedItems = { ...state.itemsByID };

    const updateChangedItems = setTransactionHiddenState(
        true,
        action.payload.listViewType
    )(updatedItems);
    const findUpdatedKeysByPurchaseOrderId =
        findKeysByPurchaseOrderId(updatedItems);
    action.payload.ids
        .flatMap(findUpdatedKeysByPurchaseOrderId)
        .map(updateChangedItems);
    return updatedItems;
};

export const markTransactionAsShownBySellerReducer = (state, action) => {
    const updatedItems = { ...state.itemsByID };

    const updateChangedItems = setTransactionHiddenState(
        false,
        action.payload.listViewType
    )(updatedItems);
    const findUpdatedKeysByPurchaseOrderId =
        findKeysByPurchaseOrderId(updatedItems);
    action.payload.ids
        .flatMap(findUpdatedKeysByPurchaseOrderId)
        .map(updateChangedItems);
    return updatedItems;
};

export const markItemAsHiddenReducer = (state, action) => {
    const updatedItems = { ...state.itemsByID };
    const updateChangedItems = setUnsoldItemHiddenState(
        true,
        action.payload.listViewType
    )(updatedItems, state.currentMemberId);
    const findUpdatedKeysByItemId = findKeysByItemId(updatedItems);
    action.payload.ids.map(findUpdatedKeysByItemId).map(updateChangedItems);
    return updatedItems;
};

export const markItemAsShownReducer = (state, action) => {
    const updatedItems = { ...state.itemsByID };
    const updateChangedItems = setUnsoldItemHiddenState(
        false,
        action.payload.listViewType
    )(updatedItems);
    const findUpdatedKeysByItemId = findKeysByItemId(updatedItems);
    action.payload.ids.map(findUpdatedKeysByItemId).map(updateChangedItems);
    return updatedItems;
};

export const feedbackLeftBySeller = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setFeedback(
        true,
        action.payload.listViewType
    )(stateItems);
    const findUpdatedKeysByTransactionId = findKeysByTransactionId(stateItems);
    const updatedItems = [action.payload.id]
        .map(findUpdatedKeysByTransactionId)
        .map(updateChangedItems);

    updatedItems.forEach((updatedItem) => {
        stateItems[updatedItem.key] = updatedItem;
    });

    return stateItems;
};

export const markAsShippingBooked = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setShippingBookedState(
        true,
        action.payload.listViewType
    )(stateItems);

    const findUpdatedKeysByTransactionId = findKeysByTransactionId(stateItems);
    const updatedItems = action.payload.ids
        .map(findUpdatedKeysByTransactionId)
        .map(updateChangedItems);

    updatedItems.forEach((updatedItem) => {
        stateItems[updatedItem.key] = updatedItem;
    });

    return stateItems;
};

export const unmarkAsShippingBooked = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setShippingBookedState(
        false,
        action.payload.listViewType
    )(stateItems);
    const findUpdatedKeysByTransactionId = findKeysByTransactionId(stateItems);
    const updatedItems = action.payload.ids
        .map(findUpdatedKeysByTransactionId)
        .map(updateChangedItems);

    updatedItems.forEach((updatedItem) => {
        stateItems[updatedItem.key] = updatedItem;
    });

    return stateItems;
};

export const deleteShipment = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setShippingDeletedState(
        action.payload.listViewType
    )(stateItems);
    const findUpdatedKeysByPurchaseOrderId =
        findKeysByPurchaseOrderId(stateItems);

    const updatedItems = [action.payload.purchaseOrderId]
        .map(findUpdatedKeysByPurchaseOrderId)
        .map(updateChangedItems);

    updatedItems.forEach((updatedItem) => {
        stateItems[updatedItem.key] = updatedItem;
    });

    return stateItems;
};

export const setRestartedReducer = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setItemsHasRestartedChild(
        true,
        action.payload.listViewType
    )(stateItems);
    const findUpdatedKeysByItemId = findKeysByItemId(stateItems);
    const updatedItems = action.payload.itemIds
        .map(findUpdatedKeysByItemId)
        .map(updateChangedItems);
    updatedItems.forEach((updatedItem) => {
        stateItems[updatedItem.key] = updatedItem;
    });
    return stateItems;
};

export const feedbacksLeftBySeller = (state, action) => {
    const stateItems = { ...state.itemsByID };
    const updateChangedItems = setFeedback(
        true,
        action.payload.listViewType
    )(stateItems);
    const findUpdatedKeysByTransactionId = findKeysByTransactionId(stateItems);
    const updatedItems = action.payload.submittables
        .map((submittable) => submittable.transactionId)
        .map(findUpdatedKeysByTransactionId)
        .map(updateChangedItems);

    updatedItems.forEach((updatedItem) => {
        stateItems[updatedItem.key] = updatedItem;
    });

    return stateItems;
};
